import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  BookOpen,
  ClipboardCheck,
  Library,
  LogOut,
  TrendingUp,
  User,
  Video,
} from 'lucide-react';
import { SidebarItem } from './SidebarItem.jsx';
import { OrgBadge } from './OrgBadge.jsx';
import { Brand } from '../ui/Brand.jsx';
import { ConfirmDialog } from '../ui/ConfirmDialog.jsx';
import { signedOut } from '../features/auth/store/authSlice.js';
import { isB2BUser } from '../features/auth/lib/onboardingStatus.js';
import {
  selectCanShowCoachCalls,
  selectCanShowMyAssessments,
  selectCanShowMyCourses,
  selectCanShowProgress,
  selectCanShowSettings,
  selectFeatureVisibilityReady,
  selectHasVisibleCoachCourse,
} from '../features/appVisibility/store/selectors.js';
import { cn } from '../lib/cn.js';
import { isNativePlatform } from '../services/native/platformService.js';

function SectionEyebrow({ children }) {
  return (
    <div className="sb-section">
      <span className="ds-eyebrow">{children}</span>
    </div>
  );
}

export function Sidebar({ mobile = false, onNavigate, waitForFeatureVisibility = false }) {
  const dispatch = useDispatch();
  const [signOutOpen, setSignOutOpen] = useState(false);
  const isB2B = useSelector((s) => isB2BUser({ user: s.auth.user, org: s.auth.org }));
  const canShowCoachCalls = useSelector(selectCanShowCoachCalls);
  const canShowMyCourses = useSelector(selectCanShowMyCourses);
  const canShowMyAssessments = useSelector(selectCanShowMyAssessments);
  const canShowProgress = useSelector(selectCanShowProgress);
  const canShowSettings = useSelector(selectCanShowSettings);
  const featureVisibilityReady = useSelector(selectFeatureVisibilityReady);
  const hasCoachingCourse = useSelector(selectHasVisibleCoachCourse);
  const hideBottomNavDuplicates = mobile && isNativePlatform();
  const canRenderNavigation = !waitForFeatureVisibility || featureVisibilityReady;
  const showLearnSection = canRenderNavigation && !hideBottomNavDuplicates
    && (canShowMyCourses || canShowMyAssessments || canShowCoachCalls);
  const showMoreSection = canRenderNavigation && !hideBottomNavDuplicates
    && (!isB2B || canShowSettings);

  return (
    <aside className={cn('sb', mobile && 'is-mobile')}>
      <div className="sb-head">
        <div className="sb-brand"><Brand /></div>
        <OrgBadge />
      </div>

      {showLearnSection && (
        <>
          <SectionEyebrow>Learn</SectionEyebrow>
          {canShowMyCourses && (
            <SidebarItem to="/courses" label="My Courses" icon={<BookOpen size={16} strokeWidth={2} />} onNavigate={onNavigate} />
          )}
          {canShowMyAssessments && (
            <SidebarItem to="/assessments" label="My Assessments" icon={<ClipboardCheck size={16} strokeWidth={2} />} onNavigate={onNavigate} />
          )}
          {canShowCoachCalls && (
            <SidebarItem
              to="/coaching"
              label="My Coach Calls"
              icon={<Video size={16} strokeWidth={2} />}
              disabled={!hasCoachingCourse}
              hint={!hasCoachingCourse ? 'Available with premium' : undefined}
              onNavigate={onNavigate}
            />
          )}
        </>
      )}

      {canRenderNavigation && canShowProgress && (
        <>
          <SectionEyebrow>Track</SectionEyebrow>
          <SidebarItem to="/progress" label="Progress" icon={<TrendingUp size={16} strokeWidth={2} />} onNavigate={onNavigate} />
        </>
      )}

      {showMoreSection && (
        <>
          <SectionEyebrow>More</SectionEyebrow>
          {!isB2B && !hideBottomNavDuplicates && (
            <SidebarItem to="/library" label="More Courses" tag="Buy" icon={<Library size={16} strokeWidth={2} />} onNavigate={onNavigate} />
          )}
          {canShowSettings && !hideBottomNavDuplicates && (
            <SidebarItem to="/account" label="Account" icon={<User size={16} strokeWidth={2} />} onNavigate={onNavigate} />
          )}
        </>
      )}

      {canRenderNavigation && (
        <div className="sb-foot">
          <button type="button" onClick={() => setSignOutOpen(true)}>
            <LogOut aria-hidden size={15} strokeWidth={2} />
            <span>Sign out</span>
          </button>
        </div>
      )}

      <ConfirmDialog
        open={signOutOpen}
        tone="danger"
        title="Sign out?"
        description="You will need to sign in again to continue your courses and assessments."
        cancelLabel="Stay signed in"
        confirmLabel="Sign out"
        onCancel={() => setSignOutOpen(false)}
        onConfirm={() => {
          setSignOutOpen(false);
          dispatch(signedOut());
        }}
      />
    </aside>
  );
}
